import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { styled } from '@mui/material';

import useGoogleAnalytics from '@/hooks/useGoogleAnalytics';
import { useWomPricing } from '@/hooks/useWomPricing';

import { GoogleAnalyticsEventName } from '@montugroup/circuit-shared/types/analytics';
import { Button, ViewObserver } from '@montugroup/design-system';
import { PatientReferralActionType, usePatientReferral } from '../../context/PatientReferral';

type Props = {
  isNavBanner: boolean;
};

const NavBannerButton = styled(Button)(({ theme }) => ({
  borderRadius: '0',
  fontSize: '14px',
  textDecoration: 'underline',
  justifyContent: 'flex-start',
  paddingLeft: '0.5rem',
  paddingRight: '0',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.common.white} !important`,
  },
}));

const BannerButton = styled(Button)<{ sidebar: 'true' | 'false' }>(({ theme, sidebar }) => ({
  borderRadius: '0',
  fontSize: '14px',
  textDecoration: 'underline',
  position: 'fixed',
  bottom: 0,
  zIndex: 100,
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: `${theme.palette.secondary.main} !important`,
    color: `${theme.palette.common.white} !important`,
  },
  [theme.breakpoints.up('md')]: {
    position: sidebar === 'true' ? 'static' : 'fixed',
    bottom: sidebar === 'true' ? 'auto' : 0,
  },
}));

function PatientReferralNavBanner({ isNavBanner }: Props) {
  const { dispatch, open } = usePatientReferral();
  const { sendGoogleAnalyticsGTagEvent } = useGoogleAnalytics();
  const { discount: womPrice, isOverrideActive, copyVariant } = useWomPricing();
  const handleOnBannerClick = () => {
    if (!open) {
      sendGoogleAnalyticsGTagEvent(GoogleAnalyticsEventName.STC_BANNER_CLICK, {
        event_category: 'stc_banner_click',
        event_label: 'stc_banner_cta',
      });
    }

    dispatch({ type: PatientReferralActionType.TOGGLE_OPEN });
  };

  // An override only lasts until its end date, so its copy says the offer is limited time (kept short for mobile)
  const bannerText =
    copyVariant === 'increased'
      ? `Share the Care - New $${womPrice} off, limited time`
      : isOverrideActive
        ? `Share the Care - $${womPrice} off for a limited time`
        : `Share the Care - Enjoy $${womPrice} off with Alternaleaf`;

  return isNavBanner ? (
    <ViewObserver id="patient-referral-nav-banner">
      <NavBannerButton fullWidth onClick={handleOnBannerClick}>
        <GroupOutlinedIcon
          style={{
            marginRight: '0.5rem',
            fontSize: '1.5rem',
          }}
        />
        {bannerText}
      </NavBannerButton>
    </ViewObserver>
  ) : (
    <ViewObserver id="patient-referral-banner-bottom">
      <BannerButton fullWidth onClick={handleOnBannerClick} sidebar="true">
        <GroupOutlinedIcon
          style={{
            marginRight: '0.5rem',
            fontSize: '1.5rem',
          }}
        />
        {bannerText}
      </BannerButton>
    </ViewObserver>
  );
}

export default PatientReferralNavBanner;
